import { useMemo } from 'react';
import { useSelector } from '@xstate/store/react';
import { useCanvasStores } from '../stores/CanvasContext';
import { useResourcesForCanvas } from '../../stores';
import { getAttachedResourceIds } from '../entityTypes';
import type { CanvasEntity } from '../entityTypes';
import type { ResourceDefinition, ResourceFieldType, ResourceAggregation } from '../../types';
import { computeAggregate } from '../../lib/formatFieldValue';

export interface ResourceUsageRow {
  resourceId: string;
  resource: ResourceDefinition;
  entityIds: string[];
  entityCount: number;
  entityNames: Map<string, string>; // entityId -> display name
  fieldValues: Map<string, number>; // fieldId -> raw value
}

export interface FieldColumn {
  fieldId: string;
  key: string;
  type: ResourceFieldType;
  unit?: string;
  interval?: string;
  aggregate: ResourceAggregation;
}

export interface OverviewSummary {
  totalResources: number;
  totalEntitiesWithResources: number;
  totalCostMonthly: number | null;
  totalCostYearly: number | null;
}

export interface OverviewData {
  rows: ResourceUsageRow[];
  fieldColumns: FieldColumn[];
  summary: OverviewSummary;
  totals: Map<string, number>; // fieldId -> aggregated total
  categories: string[];        // all unique categories
  fieldTypes: ResourceFieldType[]; // all unique field types
}

function getEntityDisplayName(entity: CanvasEntity): string {
  const data = entity.data as Record<string, unknown>;
  if (entity.kind === 'task' && typeof data.taskId === 'string') return data.taskId;
  if (entity.kind === 'group' && typeof data.label === 'string') return data.label;
  if (entity.kind === 'textblock' && typeof data.text === 'string') return data.text.slice(0, 30);
  if (entity.kind === 'textlabel' && typeof data.text === 'string') return data.text.slice(0, 30);
  if (entity.kind === 'blocknode') return 'Block';
  return entity.kind;
}

export function useResourceCanvasOverview(): OverviewData {
  const { store, boardId } = useCanvasStores();
  const resources = useResourcesForCanvas(boardId);

  // Get all raw node entities (including groups) for the canvas
  const nodes = useSelector(store, s => s.context.nodes);
  const groups = useSelector(store, s => s.context.groups);
  const rawEntities = useMemo(() => [
    ...Object.values(nodes),
    ...Object.values(groups),
  ], [nodes, groups]);

  return useMemo(() => {
    const resourceMap = new Map<string, ResourceDefinition>(resources.map(r => [r.id, r]));

    // 1. Collect resourceId -> entityIds mapping
    const resourceEntityMap = new Map<string, { entityIds: string[]; entityNames: Map<string, string> }>();

    for (const entity of rawEntities) {
      if (entity.kind === 'connector') continue;
      const attachedIds = getAttachedResourceIds(entity);
      for (const rid of attachedIds) {
        if (!resourceMap.has(rid)) continue;
        let entry = resourceEntityMap.get(rid);
        if (!entry) {
          entry = { entityIds: [], entityNames: new Map() };
          resourceEntityMap.set(rid, entry);
        }
        entry.entityIds.push(entity.id);
        entry.entityNames.set(entity.id, getEntityDisplayName(entity));
      }
    }

    // 2. Build rows
    const rows: ResourceUsageRow[] = [];
    for (const [resourceId, { entityIds, entityNames }] of resourceEntityMap) {
      const resource = resourceMap.get(resourceId);
      if (!resource) continue;

      const fieldValues = new Map<string, number>();
      for (const field of resource.fields) {
        if (field.type === 'text') continue;
        if (typeof field.value !== 'number') continue;
        // Use key+type as identifier so same fields across resources share a column
        fieldValues.set(`${field.key}\0${field.type}`, field.value);
      }

      rows.push({
        resourceId,
        resource,
        entityIds,
        entityCount: entityIds.length,
        entityNames,
        fieldValues,
      });
    }

    // 3. Build dynamic field columns (de-duplicated by key+type across all resources)
    const fieldColumnMap = new Map<string, FieldColumn>();
    for (const row of rows) {
      for (const field of row.resource.fields) {
        if (field.type === 'text') continue;
        if (typeof field.value !== 'number') continue;
        const columnKey = `${field.key}\0${field.type}`;
        if (!fieldColumnMap.has(columnKey)) {
          fieldColumnMap.set(columnKey, {
            fieldId: columnKey,
            key: field.key,
            type: field.type,
            unit: field.unit,
            interval: field.interval,
            aggregate: field.aggregate,
          });
        }
      }
    }
    const fieldColumns = Array.from(fieldColumnMap.values());
    // Sort: currency first, then number, then rest
    const typeOrder: Record<string, number> = { currency: 0, number: 1, percentage: 2, duration: 3 };
    fieldColumns.sort((a, b) => (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99));

    // 4. Compute summary
    let totalCostMonthly = 0;
    let totalCostYearly = 0;
    let hasMonthlyCost = false;
    let hasYearlyCost = false;
    const entitiesWithResources = new Set<string>();

    for (const row of rows) {
      for (const eid of row.entityIds) entitiesWithResources.add(eid);
      for (const field of row.resource.fields) {
        if (field.type === 'currency' && typeof field.value === 'number') {
          if (field.interval === 'monthly') {
            totalCostMonthly += field.value * row.entityCount;
            hasMonthlyCost = true;
          } else if (field.interval === 'yearly') {
            totalCostYearly += field.value * row.entityCount;
            hasYearlyCost = true;
          }
        }
      }
    }

    const summary: OverviewSummary = {
      totalResources: rows.length,
      totalEntitiesWithResources: entitiesWithResources.size,
      totalCostMonthly: hasMonthlyCost ? totalCostMonthly : null,
      totalCostYearly: hasYearlyCost ? totalCostYearly : null,
    };

    // 5. Compute totals per field column
    const totals = new Map<string, number>();
    for (const col of fieldColumns) {
      const values: number[] = [];
      for (const row of rows) {
        const val = row.fieldValues.get(col.fieldId);
        if (val !== undefined) {
          values.push(col.aggregate === 'sum' ? val * row.entityCount : val);
        }
      }
      const result = computeAggregate(col.aggregate, values);
      if (result !== undefined) totals.set(col.fieldId, result);
    }

    // 6. Collect unique categories and field types
    const categorySet = new Set<string>();
    for (const row of rows) {
      if (row.resource.category) categorySet.add(row.resource.category);
    }
    const categories = Array.from(categorySet).sort();

    const fieldTypeSet = new Set<ResourceFieldType>();
    for (const col of fieldColumns) fieldTypeSet.add(col.type);
    const fieldTypes = Array.from(fieldTypeSet);

    return { rows, fieldColumns, summary, totals, categories, fieldTypes };
  }, [rawEntities, resources]);
}
